// @flow
import * as React from 'react'
import styled from 'styled-components'
import { colors } from '@components/shared'
import { Col, Visible, Container, Row, config } from 'react-awesome-styled-grid'
import { getBackground } from '../helpers'
import type { BackgroundType } from '../helpers'
import SectionBackgroundImageParallax from './SectionBackgroundImageParallax'
import type { ContentBlockUniverseSectionType } from '../../../services/contentfulMarketingLandingPages'
import SharedCta from '../SharedCta'
import { FadeInImage } from '@components/shared/FadeInImage'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { MARKS } from '@contentful/rich-text-types'
import { landingPageBackgroundColor } from '../../../services/contentfulMarketingLandingPages'

const options = {
  renderNode: {
    [MARKS.CODE]: (node: any, children: any): React.Node => {
      return <div dangerouslySetInnerHTML={{ __html: children }} />
    }
  }
}

export default function UniverseSection (props: {
  block: ContentBlockUniverseSectionType,
  isFirst: boolean,
  isSingleContentBlock: boolean
}): React.Node {
  const {
    id,
    headerText,
    descriptionText,
    cta,
    mobileTopImage,
    mobileBottomImage,
    tabletTopImage,
    tabletBottomImage,
    desktopImage,
    backgroundColor
  } = props.block

  const description = documentToReactComponents(descriptionText, options)

  const sectionBackground: BackgroundType = getBackground(backgroundColor)
  const isWhiteText =
    backgroundColor === landingPageBackgroundColor.glow ||
    props.isSingleContentBlock

  const mobileTopImageUrl =
    mobileTopImage ||
    '//images.ctfassets.net/03of3q5qzesq/iedqnq280ynRUObk9Mzsa/f72363e120fb7b8d285e8fd47a2e6626/mobile-top.png'
  const mobileBottomImageUrl =
    mobileBottomImage ||
    '//images.ctfassets.net/03of3q5qzesq/2ajsfXnXi1aJnwHcIK4MU5/95fb67cdb7edf0a68c1f01303725f4dc/mobile-bottom.png'
  const tabletTopImageUrl =
    tabletTopImage ||
    '//images.ctfassets.net/03of3q5qzesq/01MfCLnQHH9gJux7MWz9kt/d5d4b2fa47a7103683001f13e9b15c28/tablet-top.png'
  const tabletBottomImageUrl =
    tabletBottomImage ||
    '//images.ctfassets.net/03of3q5qzesq/33NNPvzmhdQUwR2RQGGCAi/5a402ebefab66712cfa6e1253a81eb8b/tablet-bottom.png'
  const desktopImageUrl =
    desktopImage ||
    '//images.ctfassets.net/03of3q5qzesq/5PxdDAhw6dRrGA658Sdm8f/81a4d08018ccd9a4437a48b2b0fcdcf2/desktop-image.png'

  return (
    <Section
      backgroundColor={
        sectionBackground.backgroundImageUrl
          ? 'unset'
          : sectionBackground.backgroundColor
      }
      className={
        props.isSingleContentBlock
          ? 'single-content-block'
          : props.isFirst
            ? 'first-block'
            : ''
      }
    >
      <div className='main-section'>
        <Visible xs={true} sm={false} md={false} lg={false} xl={false}>
          <FadeInImage
            src={`https:${mobileTopImageUrl}`}
            height={450}
            width={750}
            fadeInTime={0.3}
          />
        </Visible>
        <Visible xs={false} sm={true} md={true} lg={false} xl={false}>
          <FadeInImage
            src={`https:${tabletTopImageUrl}`}
            height={400}
            width={1536}
            fadeInTime={0.3}
          />
        </Visible>
        <Container>
          <Row align='center'>
            <Col
              xs={4}
              sm={8}
              md={6}
              lg={4}
              xl={4}
              align='center'
              offset={{ md: 1, lg: 0.5 }}
            >
              <TextContainer>
                {!!headerText && (
                  <Header isWhiteText={isWhiteText}>{headerText}</Header>
                )}
                {!!description && (
                  <Description isWhiteText={isWhiteText}>
                    {description}
                  </Description>
                )}
                {!!cta && (
                  <CtaContainer>
                    <SharedCta cta={cta} />
                  </CtaContainer>
                )}
              </TextContainer>
            </Col>

            <Visible xs={false} sm={false} md={false} lg={true} xl={true}>
              <Col lg={6.5} align='flex-end' offset={{ lg: 1 }} noGutter>
                <DesktopImageContainer>
                  <FadeInImage
                    src={`https:${desktopImageUrl}`}
                    height={1560}
                    width={1400}
                    fadeInTime={0.3}
                  />
                </DesktopImageContainer>
              </Col>
            </Visible>
          </Row>
        </Container>
        <Visible xs={true} sm={false} md={false} lg={false} xl={false}>
          <FadeInImage
            src={`https:${mobileBottomImageUrl}`}
            height={600}
            width={750}
            fadeInTime={0.3}
          />
        </Visible>
        <Visible xs={false} sm={true} md={true} lg={false} xl={false}>
          <FadeInImage
            src={`https:${tabletBottomImageUrl}`}
            height={400}
            width={1536}
            fadeInTime={0.3}
          />
        </Visible>

        {sectionBackground.backgroundImageUrl &&
          sectionBackground.height &&
          sectionBackground.width && (
          <SectionBackgroundImageParallax
            uniqueId={id}
            backgroundColor={sectionBackground.backgroundColor}
            imageUrl={sectionBackground.backgroundImageUrl}
            height={sectionBackground.height}
            width={sectionBackground.width}
          />
        )}
      </div>
    </Section>
  )
}

const Section = styled.section`
  background-color: ${(props: any): string => props.backgroundColor};
  margin: 0;
  overflow: hidden;
  position: relative;

  padding: 70px 0;
  ${(props: any): string => config(props).media.md`
    padding: 80px 0;
  `}
  ${(props: any): string => config(props).media.xl`
    padding: 100px 0;
  `}
 
  &.first-block {
    margin: -75px 0 0;

    padding: 145px 0 70px;
    ${(props: any): string => config(props).media.md`
      padding: 155px 0 80px;
    `}
    ${(props: any): string => config(props).media.md`
      padding: 175px 0 100px;
    `}
  }

  &.single-content-block {
    align-items: center;
    display: flex;
    min-height: 100vh;
    left: 0;
    margin: 0;
    padding: 0;
    position: absolute;
    right: 0;
    top: 0;

    div.main-section {
      padding: 130px 0;
      width: 100%;
    }
  }
`
const DesktopImageContainer = styled.div`
  margin: 0 -60px 0 0;
  max-width: 750px;
  width: calc(100% + 60px);
`
const TextContainer = styled.div`
  margin: 40px 0;
  max-width: 900px;
`
const Header = styled.h1`
  color: ${(props: any): string =>
    props.isWhiteText ? colors.white : colors.sand};
  font-family: 'Queens Condensed Light';
  font-style: normal;
  font-weight: normal;
  line-height: 120%;
  margin: 0 0 20px 0;
  padding: 0;
  text-align: center;

  font-size: 36px;
  ${(props: any): string => config(props).media.md`
    font-size: 50px;
  `}
  ${(props: any): string => config(props).media.lg`
    font-size: 60px;
    text-align: left;
  `}
`
const Description = styled.div`
  color: ${(props: any): string =>
    props.isWhiteText ? colors.white : colors.sand};
  font-family: 'Good Sans Light';
  font-style: normal;
  font-weight: normal;
  line-height: 150%;
  padding: 0;
  margin: 0;
  text-align: center;

  font-size: 16px;
  ${(props: any): string => config(props).media.md`
    font-size: 18px;
  `}
  ${(props: any): string => config(props).media.lg`
    text-align: left;
  `}

  ol, li, ul, blockquote,img {
    display: none;
  }
  b {
    font-family: 'Good Sans Bold';
  }
`
const CtaContainer = styled.div`
  display: flex;
  justify-content: center;

  margin: 30px 0 0 0;
  ${(props: any): string => config(props).media.md`
    margin: 35px 0 0 0;
  `}
  ${(props: any): string => config(props).media.lg`
    justify-content: flex-start;
  `}
`
